import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Mail, Trash2, UserPlus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { UnopenedSupporters } from "@/components/UnopenedSupporters";
import { PageHeader, SectionTitle, Empty } from "@/components/kit";
import { Switch } from "@/components/ui/switch";
import { SupporterEmailPreview } from "@/components/SupporterEmailPreview";
import { RenewalRequests } from "@/components/RenewalRequests";
import { RenewDialog } from "@/components/RenewDialog";

const PERMS = [["share_mood_trends", "Mood trends"], ["share_streaks", "Streak progress"], ["share_weekly_summary", "Weekly summary"], ["weekly_email", "Weekly email recap"], ["tough_day_prompts", "Tough-day prompts"], ["gratitude_notes", "Gratitude notes"], ["open_when_letters", "Open-When letters"], ["help_card", "How to help me card"], ["streak_hugs", "Skill streak hugs"], ["forecast_heads_up", "Harder-day heads-up"]];

const daysLeft = (d) => Math.ceil((new Date(d.endsWith?.("Z") ? d : `${d}Z`) - Date.now()) / 86400000);

const LinkExpiry = ({ s, onRenew }) => {
  if (!s.expires_at) return null;
  const days = daysLeft(s.expires_at);
  const label = days <= 0 ? "Link expired" : `Link expires in ${days} day${days === 1 ? "" : "s"}`;
  return (
    <div className="flex flex-wrap items-center gap-3 mt-4 text-sm" data-testid={`supporter-expiry-${s.id}`}>
      <span className={days <= 7 ? "text-amber-700 font-medium" : "text-mm-muted"}>{label}</span>
      {days <= 30 && <button className="mm-btn-ghost !py-1 !px-3 text-sm bg-mm-surface" onClick={onRenew} data-testid={`supporter-renew-${s.id}`}>Renew for 90 days</button>}
    </div>
  );
};

export default function Supporters() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", share_mood_trends: true, share_streaks: true, share_weekly_summary: true, weekly_email: true, tough_day_prompts: false, gratitude_notes: true, open_when_letters: true, help_card: true, streak_hugs: true, forecast_heads_up: true });

  const [rev, setRev] = useState(0);
  const load = () => { setRev((n) => n + 1); return api.get("/supporters").then((r) => setList(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/supporters", form);
      if (data.invite_sent) toast.success(`${form.name} added. We emailed them their private link.`);
      else toast.warning(`${form.name} added, but the invite email couldn't be sent. Copy their link and send it yourself.`);
      setForm({ ...form, name: "", email: "" }); load();
    }
    catch (err) { toast.error(errMsg(err)); }
  };
  const update = async (s, k, v) => { await api.put(`/supporters/${s.id}`, { [k]: v }); load(); };
  const revoke = async (s) => { await api.delete(`/supporters/${s.id}`); toast.success("Access revoked"); load(); };
  const [renewing, setRenewing] = useState(null);
  const [invitingAll, setInvitingAll] = useState(false);
  const weeklyAll = async () => {
    try {
      const { data } = await api.post("/supporters/weekly-all", { enabled: true });
      load();
      toast.success(`Weekly updates on for ${data.changed.length} supporter${data.changed.length === 1 ? "" : "s"}`, {
        duration: 8000, action: { label: "Undo", onClick: () => api.post("/supporters/weekly-all", { enabled: false, ids: data.changed }).then(load).catch((e) => toast.error(errMsg(e))) },
      });
    } catch (e) { toast.error(errMsg(e)); }
  };
  const inviteAll = async () => {
    if (!window.confirm(`Email the invite and private link to all ${list.length} supporter${list.length === 1 ? "" : "s"}?`)) return;
    setInvitingAll(true);
    try {
      const { data } = await api.post("/supporters/invite-all");
      if (data.sent.length) toast.success(`Invites emailed to ${data.sent.join(", ")}`);
      if (data.skipped.length) toast(`Already sent 3 times today: ${data.skipped.join(", ")}`);
      if (data.failed.length) toast.error(`Couldn't email ${data.failed.join(", ")}. Copy their links instead.`);
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setInvitingAll(false); }
  };
  const invite = async (s) => { try { const { data } = await api.post(`/supporters/${s.id}/invite`); toast.success(`Invite emailed to ${data.email}`); load(); } catch (e) { toast.error(errMsg(e)); } };
  const copy = (link) => { navigator.clipboard.writeText(link); toast.success("Secure link copied"); };

  return (
    <div className="max-w-3xl" data-testid="supporters-page">
      <PageHeader back title="Trusted Supporters" subtitle="Share progress with a therapist, coach or loved one — read-only, revocable anytime" />
      <form onSubmit={add} className="mm-card p-6">
        <h3 className="font-semibold flex items-center gap-2"><UserPlus size={18} />Invite a supporter</h3>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <input className="mm-input" required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supporter-name-input" />
          <input className="mm-input" type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="supporter-email-input" />
        </div>
        <div className="flex flex-wrap gap-5 mt-4">
          {PERMS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: v })} data-testid={`supporter-perm-${k}`} />{l}</label>)}
        </div>
        <p className="text-xs text-mm-muted mt-3">🔒 Journal entries are never shared. Weekly email recaps go out on your recap day and include only what you share. Tough-day prompts send a gentle "a kind note today could help" email (no details) when you open your plan on a nudge day or tap "Tell my supporters".</p>
        <button className="mm-btn mt-4" data-testid="supporter-add-button">Create secure link</button>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Your supporters</SectionTitle>
        {list.length > 0 && <div className="flex flex-wrap gap-2">
        {list.some((s) => !s.weekly_email) && <button onClick={weeklyAll} className="mm-btn-ghost !py-2 text-sm" data-testid="supporters-weekly-all">📬 Turn on weekly updates for all</button>}
        <button onClick={inviteAll} disabled={invitingAll} className="mm-btn-ghost !py-2 text-sm" data-testid="supporters-invite-all">{invitingAll ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}Email invites to all</button></div>}
      </div>
      <UnopenedSupporters refresh={rev} />
      <RenewalRequests key={rev} onChange={load} />
      <RenewDialog supporter={renewing} onClose={() => setRenewing(null)} onDone={load} />
      <div className="space-y-3">
        {list.length ? list.map((s) => (
          <div key={s.id} className="mm-card p-5" data-testid={`supporter-${s.id}`}>
            <div className="flex justify-between items-start gap-3">
              <div><div className="font-semibold">{s.name}</div><div className="text-sm text-mm-muted">{s.email}</div>
                {s.invite_error ? <div className="text-xs text-mm-error" data-testid={`supporter-invite-status-${s.id}`}>Invite email didn't send. Tap ✉ to retry, or copy the link</div>
                  : s.invited_at ? <div className="text-xs text-mm-muted" data-testid={`supporter-invite-status-${s.id}`}>Invite emailed {new Date(s.invited_at).toLocaleDateString()}</div> : null}</div>
              <button className="mm-btn mm-btn-danger !py-2 !px-4 text-sm" onClick={() => revoke(s)} data-testid={`supporter-revoke-${s.id}`}><Trash2 size={14} />Revoke</button>
            </div>
            <div className="flex flex-wrap gap-5 mt-4">
              {PERMS.map(([k, l]) => <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={!!s[k]} onCheckedChange={(v) => update(s, k, v)} data-testid={`supporter-${s.id}-${k}`} />{l}</label>)}
            </div>
            <LinkExpiry s={s} onRenew={() => setRenewing(s)} />
            {s.thanks_heart_at && <p className="text-sm text-mm-primary mt-2" data-testid={`supporter-thanks-heart-${s.id}`}>💜 {s.name} sent a heart for your thank-you · {new Date(s.thanks_heart_at.endsWith?.("Z") ? s.thanks_heart_at : `${s.thanks_heart_at}Z`).toLocaleDateString()}</p>}
            {s.weekly_email && <SupporterEmailPreview id={s.id} />}
            <div className="mm-soft p-3 mt-4 flex items-center gap-2">
              <code className="text-xs truncate flex-1" data-testid={`supporter-link-${s.id}`}>{s.access_link}</code>
              <button className="mm-btn-ghost !p-2 bg-mm-surface" onClick={() => invite(s)} title={s.invited_at ? `Invite emailed ${new Date(s.invited_at).toLocaleDateString()}. Send again` : "Email the invite"} aria-label="Resend invite email" data-testid={`supporter-invite-${s.id}`}><Mail size={14} /></button>
              <button className="mm-btn-ghost !p-2 bg-mm-surface" onClick={() => copy(s.access_link)} data-testid={`supporter-copy-${s.id}`}><Copy size={14} /></button>
            </div>
          </div>
        )) : <Empty emoji="🤝" title="No supporters yet" text="Invite someone you trust to see your progress." />}
      </div>
    </div>
  );
}
